import type { ArchetypeId, SlotId, SubSpecId } from '../model/types.ts';
import {
  KEYSTONE_RANKS,
  type PassiveNode,
  type SpecRole,
  type SynergyTag,
  type WhisperPassive,
} from './classDesign.ts';

export interface AbilityDef {
  id: string;
  name: string;
  kind: 'damage' | 'aoe' | 'heal' | 'shield' | 'sunder';
  /** Multiplier on attack (damage) or heal power (heal). Shield uses this as a fraction of max HP. */
  coeff: number;
  cooldownTicks: number;
  /** Armor stacks removed. Scaled again by the slot that equips the sub-spec. */
  strip: number;
  /** Share of the hit that ignores armor-stack damage reduction. */
  ignoreDr: number;
  tags: SynergyTag[];
}

export interface SubSpecDef {
  id: SubSpecId;
  name: string;
  archetype: ArchetypeId;
  /** Primary role (●). */
  role: SpecRole;
  /** Secondary role (○). Null when the spec has none. */
  secondaryRole: SpecRole | null;
  fantasy: string;
  /** Stat weights. The slot budget scales them. */
  profile: {
    hp: number;
    attack: number;
    armor: number;
    armorBreak: number;
    heal: number;
  };
  /** Secondary slot uses both. Tertiary uses the first. Primary also gains the ultimate. */
  abilities: [AbilityDef, AbilityDef];
  ultimate: AbilityDef;
  passives: PassiveNode[];
  whisper: WhisperPassive;
  /** Indexes the fusion passive when this spec is the Heart Mark of a hybrid. */
  fusionVariant: 0 | 1 | 2;
  tags: SynergyTag[];
  preset: {
    abilityId: string;
    condition:
      | { kind: 'always' }
      | { kind: 'selfHpBelow'; pct: number }
      | { kind: 'enemyCountAtLeast'; count: number }
      | { kind: 'targetIsEliteOrBoss' }
      | { kind: 'targetHasArmor' };
    target: 'self' | 'lowestHp' | 'highestHp' | 'boss';
    /** Lower runs first. */
    priority: number;
  };
}

interface SpecInput {
  id: SubSpecId;
  name: string;
  archetype: ArchetypeId;
  role: SpecRole;
  secondaryRole: SpecRole | null;
  fantasy: string;
  profile: SubSpecDef['profile'];
  abilities: [AbilityDef, AbilityDef];
  ultimate: AbilityDef;
  lines: [string, string, string, string];
  whisper: string;
  fusionVariant: 0 | 1 | 2;
  tags: SynergyTag[];
  preset: SubSpecDef['preset'];
}

function passives(name: string, lines: [string, string, string, string]): PassiveNode[] {
  const ranks = [1, KEYSTONE_RANKS[0], KEYSTONE_RANKS[1], KEYSTONE_RANKS[2]];
  const titles = ['Foundation', 'Keystone', 'Apex', 'Capstone'];
  return lines.map((text, index) => ({
    name: `${name} ${titles[index]}`,
    rank: ranks[index]!,
    keystone: index > 0,
    text,
  }));
}

function make(input: SpecInput): SubSpecDef {
  return {
    id: input.id,
    name: input.name,
    archetype: input.archetype,
    role: input.role,
    secondaryRole: input.secondaryRole,
    fantasy: input.fantasy,
    profile: input.profile,
    abilities: input.abilities,
    ultimate: input.ultimate,
    passives: passives(input.name, input.lines),
    whisper: { name: `${input.name} Whisper`, text: input.whisper },
    fusionVariant: input.fusionVariant,
    tags: input.tags,
    preset: input.preset,
  };
}

function ability(
  id: string,
  name: string,
  kind: AbilityDef['kind'],
  coeff: number,
  cooldownTicks: number,
  tags: SynergyTag[],
  ignoreDr = 0,
  strip = 0,
): AbilityDef {
  return { id, name, kind, coeff, cooldownTicks, strip, ignoreDr, tags };
}

/** Role kits stay inside the bands the six launch specs already use. */
function tankKit(id: string, names: [string, string, string], tags: SynergyTag[]): Pick<SpecInput, 'abilities' | 'ultimate' | 'preset'> {
  return {
    abilities: [
      ability(`${id}_a`, names[0], 'shield', 0.28, 16, tags),
      ability(`${id}_b`, names[1], 'shield', 0.16, 12, tags),
    ],
    ultimate: ability(`${id}_ult`, names[2], 'aoe', 1.15, 24, tags),
    preset: { abilityId: `${id}_a`, condition: { kind: 'selfHpBelow', pct: 70 }, target: 'self', priority: 10 },
  };
}

function supportKit(id: string, names: [string, string, string], tags: SynergyTag[]): Pick<SpecInput, 'abilities' | 'ultimate' | 'preset'> {
  return {
    abilities: [
      ability(`${id}_a`, names[0], 'heal', 4.5, 12, tags),
      ability(`${id}_b`, names[1], 'shield', 0.12, 14, tags),
    ],
    ultimate: ability(`${id}_ult`, names[2], 'heal', 7, 24, tags),
    preset: { abilityId: `${id}_a`, condition: { kind: 'selfHpBelow', pct: 60 }, target: 'self', priority: 12 },
  };
}

function damageKit(id: string, names: [string, string, string], tags: SynergyTag[]): Pick<SpecInput, 'abilities' | 'ultimate' | 'preset'> {
  return {
    abilities: [
      ability(`${id}_a`, names[0], 'damage', 1.5, 6, tags),
      ability(`${id}_b`, names[1], 'damage', 1.25, 8, tags),
    ],
    ultimate: ability(`${id}_ult`, names[2], 'damage', 2.8, 20, tags, 0.25),
    preset: { abilityId: `${id}_a`, condition: { kind: 'always' }, target: 'lowestHp', priority: 40 },
  };
}

function breakKit(id: string, names: [string, string, string], tags: SynergyTag[]): Pick<SpecInput, 'abilities' | 'ultimate' | 'preset'> {
  const marked = [...tags, 'Armor-break'] as SynergyTag[];
  return {
    abilities: [
      ability(`${id}_a`, names[0], 'sunder', 1.65, 8, marked, 0.35, 1),
      ability(`${id}_b`, names[1], 'damage', 1.35, 10, marked),
    ],
    ultimate: ability(`${id}_ult`, names[2], 'sunder', 2.1, 22, marked, 0.5, 1),
    preset: { abilityId: `${id}_a`, condition: { kind: 'targetHasArmor' }, target: 'boss', priority: 20 },
  };
}

function controlKit(id: string, names: [string, string, string], tags: SynergyTag[]): Pick<SpecInput, 'abilities' | 'ultimate' | 'preset'> {
  return {
    abilities: [
      ability(`${id}_a`, names[0], 'aoe', 0.85, 8, tags),
      ability(`${id}_b`, names[1], 'damage', 1.3, 8, tags),
    ],
    ultimate: ability(`${id}_ult`, names[2], 'aoe', 1.5, 20, tags),
    preset: { abilityId: `${id}_a`, condition: { kind: 'enemyCountAtLeast', count: 2 }, target: 'highestHp', priority: 30 },
  };
}

const TANK = { hp: 116, attack: 8, armor: 38, armorBreak: 0, heal: 6 };
const DAMAGE = { hp: 42, attack: 30, armor: 5, armorBreak: 8, heal: 0 };
const SUPPORT = { hp: 66, attack: 9, armor: 10, armorBreak: 0, heal: 16 };
const CONTROL = { hp: 42, attack: 22, armor: 4, armorBreak: 0, heal: 0 };

export const SUBSPECS: Record<SubSpecId, SubSpecDef> = {
  bulwark: make({
    id: 'bulwark',
    name: 'Bulwark',
    archetype: 'ironbound',
    role: 'tank',
    secondaryRole: 'control',
    fantasy: 'Tower shield. Rampart spends stored guard as a shockwave.',
    profile: { hp: 120, attack: 8, armor: 40, armorBreak: 0, heal: 6 },
    abilities: [
      ability('bulwark_rampart', 'Rampart', 'shield', 0.28, 16, ['Shield']),
      ability('bulwark_brace', 'Brace', 'shield', 0.16, 12, ['Shield']),
    ],
    ultimate: ability('bulwark_shockwave', 'Shockwave', 'aoe', 1.15, 24, ['Shield']),
    lines: [
      'Blocked hits store Rampart.',
      'Rampart shield is larger.',
      'Shockwave also slows the wave.',
      'A full Rampart ignores one heavy hit.',
    ],
    whisper: 'In the Whisper slot, the first blocked hit each wave stores double Rampart.',
    fusionVariant: 0,
    tags: ['Shield'],
    preset: { abilityId: 'bulwark_rampart', condition: { kind: 'selfHpBelow', pct: 70 }, target: 'self', priority: 10 },
  }),
  chainwarden: make({
    id: 'chainwarden',
    name: 'Chainwarden',
    archetype: 'ironbound',
    role: 'control',
    secondaryRole: 'tank',
    fantasy: 'Hooks and chains that pull enemies and tether them to the player.',
    profile: CONTROL,
    ...controlKit('chainwarden', ['Hook', 'Tether', 'Drag In'], ['Bleed']),
    lines: [
      'Hook pulls the farthest foe closer.',
      'Tether shares a slice of damage across chained foes.',
      'Chained foes attack more slowly.',
      'Drag In stuns a tethered elite.',
    ],
    whisper: 'In the Whisper slot, Hook also grants a small shield.',
    fusionVariant: 1,
    tags: ['Bleed'],
  }),
  forgeheart: make({
    id: 'forgeheart',
    name: 'Forgeheart',
    archetype: 'ironbound',
    role: 'tank',
    secondaryRole: 'damage',
    fantasy: 'Heats armor between hits. Hotter armor reflects more damage but drains its own HP.',
    profile: { hp: 112, attack: 10, armor: 36, armorBreak: 0, heal: 4 },
    ...tankKit('forgeheart', ['Heat', 'Reflect', 'Cinder Pulse'], ['Shield']),
    lines: [
      'Armor heats between hits and reflects a little damage.',
      'Hot armor reflects more.',
      'Reflect no longer drains HP while a shield holds.',
      'Cinder Pulse spends the heat in an area burst.',
    ],
    whisper: 'In the Whisper slot, Heat never drops HP below 1.',
    fusionVariant: 2,
    tags: ['Shield'],
  }),
  duelist: make({
    id: 'duelist',
    name: 'Duelist of the Red Line',
    archetype: 'ironbound',
    role: 'damage',
    secondaryRole: null,
    fantasy: 'Draws a dueling circle. Damage goes up against one marked target.',
    profile: DAMAGE,
    ...damageKit('duelist', ['Mark', 'Lunge', 'Red Line'], ['Bleed']),
    lines: [
      'Mark one foe. Hits on that foe hit harder.',
      'The mark lasts longer.',
      'Lunge strikes twice on the marked foe.',
      'Red Line damage ignores a share of armor.',
    ],
    whisper: 'In the Whisper slot, Mark also bleeds the target.',
    fusionVariant: 0,
    tags: ['Bleed'],
  }),
  siegebreaker: make({
    id: 'siegebreaker',
    name: 'Siegebreaker',
    archetype: 'ironbound',
    role: 'damage',
    secondaryRole: 'control',
    fantasy: 'Slow heavy blows that break armor.',
    profile: { hp: 50, attack: 28, armor: 8, armorBreak: 36, heal: 0 },
    abilities: [
      ability('siegebreaker_sunder', 'Sunder', 'sunder', 1.65, 8, ['Armor-break'], 0.5, 1),
      ability('siegebreaker_crush', 'Crush', 'damage', 1.35, 10, ['Armor-break']),
    ],
    ultimate: ability('siegebreaker_breach', 'Breach', 'sunder', 2.3, 22, ['Armor-break'], 0.65, 1),
    lines: [
      'Sunder breaks armor stacks.',
      'Sunder removes an extra stack.',
      'Crush hits harder against broken armor.',
      'Breach ignores most of a wall of armor.',
    ],
    whisper: 'In the Whisper slot, Sunder still strips one stack.',
    fusionVariant: 0,
    tags: ['Armor-break'],
    preset: { abilityId: 'siegebreaker_sunder', condition: { kind: 'targetHasArmor' }, target: 'boss', priority: 20 },
  }),
  banner: make({
    id: 'banner',
    name: 'Banner Sergeant',
    archetype: 'ironbound',
    role: 'support',
    secondaryRole: 'tank',
    fantasy: 'Plants a war banner that steadies the bearer.',
    profile: { hp: 70, attack: 10, armor: 12, armorBreak: 0, heal: 16 },
    abilities: [
      ability('banner_rally', 'Rally', 'heal', 4.5, 12, ['Banner']),
      ability('banner_hold', 'Hold the Line', 'shield', 0.12, 14, ['Banner', 'Shield']),
    ],
    ultimate: ability('banner_standard', 'War Banner', 'heal', 7, 24, ['Banner']),
    lines: [
      'The banner steadies HP.',
      'Rally heals for more.',
      'Hold the Line lasts longer.',
      'War Banner also grants a shield.',
    ],
    whisper: 'In the Whisper slot, Rally still fires when HP is low.',
    fusionVariant: 2,
    tags: ['Banner', 'Shield'],
    preset: { abilityId: 'banner_rally', condition: { kind: 'selfHpBelow', pct: 60 }, target: 'self', priority: 12 },
  }),
  quartermaster: make({
    id: 'quartermaster',
    name: 'Quartermaster',
    archetype: 'ironbound',
    role: 'support',
    secondaryRole: 'control',
    fantasy: 'Drops supply crates (ammo, potions, temporary shields) on the field.',
    profile: SUPPORT,
    ...supportKit('quartermaster', ['Crate', 'Tonic', 'Supply Drop'], ['Shield']),
    lines: [
      'Crates drop a potion or a temporary shield.',
      'Tonic heals a little more.',
      'A crate also slows the nearest foe.',
      'Supply Drop leaves two crates.',
    ],
    whisper: 'In the Whisper slot, the first crate each fight is a shield.',
    fusionVariant: 0,
    tags: ['Shield'],
  }),
  oathkeeper: make({
    id: 'oathkeeper',
    name: 'Oathkeeper',
    archetype: 'ironbound',
    role: 'tank',
    secondaryRole: 'support',
    fantasy: 'Swears oaths before a fight. Keeping one gives a power spike. Breaking one costs a penalty.',
    profile: { hp: 118, attack: 8, armor: 38, armorBreak: 0, heal: 8 },
    ...tankKit('oathkeeper', ['Oath', 'Keep', 'Reckoning'], ['Shield']),
    lines: [
      'An oath kept raises armor. Breaking it costs HP.',
      'The kept oath lasts longer.',
      'Keep also heals a little.',
      'Reckoning refunds the oath if it was kept.',
    ],
    whisper: 'In the Whisper slot, a broken oath costs less HP.',
    fusionVariant: 1,
    tags: ['Shield'],
  }),
  loomcaster: make({
    id: 'loomcaster',
    name: 'Loomcaster',
    archetype: 'veilweaver',
    role: 'damage',
    secondaryRole: 'control',
    fantasy: 'Weaves threads between enemies. Damage to one spreads along the threads.',
    profile: DAMAGE,
    ...damageKit('loomcaster', ['Thread', 'Weave', 'Tapestry'], ['Thread']),
    lines: [
      'Threads link foes. Damage spreads along them.',
      'One more foe can be linked.',
      'Healing also spreads along threads to summons.',
      'Tapestry links the whole wave.',
    ],
    whisper: 'In the Whisper slot, Thread still links two foes.',
    fusionVariant: 0,
    tags: ['Thread'],
  }),
  hourglass: make({
    id: 'hourglass',
    name: 'Hourglass Adept',
    archetype: 'veilweaver',
    role: 'support',
    secondaryRole: 'control',
    fantasy: "Rewinds an ally's last 3 seconds (HP and position).",
    profile: SUPPORT,
    ...supportKit('hourglass', ['Rewind', 'Still', 'Reverse'], ['Time']),
    lines: [
      'Rewind restores HP from a few seconds ago.',
      'The rewind window is longer.',
      'Still delays the next enemy hit.',
      'Reverse can rewind through a heavy hit.',
    ],
    whisper: 'In the Whisper slot, Rewind restores a smaller share of HP.',
    fusionVariant: 1,
    tags: ['Time'],
  }),
  foldwalker: make({
    id: 'foldwalker',
    name: 'Fold Walker',
    archetype: 'veilweaver',
    role: 'control',
    secondaryRole: 'damage',
    fantasy: 'Short-range teleport that drags nearby enemies through the fold.',
    profile: CONTROL,
    ...controlKit('foldwalker', ['Fold', 'Drag', 'Unseam'], ['Thread']),
    lines: [
      'Fold steps a short distance and drags nearby foes.',
      'More foes are dragged.',
      'Dragged foes take a burst.',
      'Unseam drops every dragged foe together.',
    ],
    whisper: 'In the Whisper slot, Fold still drags one foe.',
    fusionVariant: 2,
    tags: ['Thread'],
  }),
  omen: make({
    id: 'omen',
    name: 'Omen Reader',
    archetype: 'veilweaver',
    role: 'support',
    secondaryRole: 'control',
    fantasy: 'Shows enemy attacks before they land. Allies who dodge them get buffs.',
    profile: { ...SUPPORT, heal: 15 },
    ...supportKit('omen', ['Omen', 'Sidestep', 'Foretold'], ['Time', 'Shield']),
    lines: [
      'Telegraphs the next hit. Dodging it grants a small buff.',
      'The buff lasts longer.',
      'Sidestep also shields.',
      'Foretold dodges a boss telegraph.',
    ],
    whisper: 'In the Whisper slot, Omen still warns of the next hit.',
    fusionVariant: 0,
    tags: ['Time', 'Shield'],
  }),
  stillpoint: make({
    id: 'stillpoint',
    name: 'Stillpoint',
    archetype: 'veilweaver',
    role: 'control',
    secondaryRole: 'damage',
    fantasy: 'Freezes zones in time. Enemies inside take delayed burst damage.',
    profile: CONTROL,
    ...controlKit('stillpoint', ['Freeze', 'Delay', 'Shatter'], ['Time']),
    lines: [
      'Freeze a zone. Foes inside take the hit later as a burst.',
      'The zone is larger.',
      'Delayed damage is higher.',
      'Shatter pops every frozen zone at once.',
    ],
    whisper: 'In the Whisper slot, Freeze still holds one foe.',
    fusionVariant: 1,
    tags: ['Time'],
  }),
  mirrortailor: make({
    id: 'mirrortailor',
    name: 'Mirror Tailor',
    archetype: 'veilweaver',
    role: 'tank',
    secondaryRole: 'control',
    fantasy: 'Stitches decoy copies that draw aggro and copy one skill.',
    profile: TANK,
    ...tankKit('mirrortailor', ['Decoy', 'Stitch', 'Double'], ['Summon', 'Shield']),
    lines: [
      'Decoys draw hits and copy one skill.',
      'The decoy lasts longer.',
      'Stitch adds a shield to the decoy.',
      'Double leaves two decoys.',
    ],
    whisper: 'In the Whisper slot, Decoy still draws one hit.',
    fusionVariant: 2,
    tags: ['Summon', 'Shield'],
  }),
  unraveler: make({
    id: 'unraveler',
    name: 'Unraveler',
    archetype: 'veilweaver',
    role: 'damage',
    secondaryRole: 'control',
    fantasy: 'Strips buffs and shields, turning them into its own damage.',
    profile: { hp: 44, attack: 26, armor: 5, armorBreak: 30, heal: 0 },
    ...breakKit('unraveler', ['Unravel', 'Strip', 'Bare Thread'], ['Thread']),
    lines: [
      'Strip a buff or shield and add it to the hit.',
      'Stripped shields add more damage.',
      'Unravel also removes armor.',
      'Bare Thread strips every shield on the target.',
    ],
    whisper: 'In the Whisper slot, Unravel still strips one shield.',
    fusionVariant: 0,
    tags: ['Thread', 'Armor-break'],
  }),
  fatebinder: make({
    id: 'fatebinder',
    name: 'Fatebinder',
    archetype: 'veilweaver',
    role: 'support',
    secondaryRole: 'tank',
    fantasy: 'Links two allies so they share damage and healing.',
    profile: SUPPORT,
    ...supportKit('fatebinder', ['Link', 'Share', 'Bound Mend'], ['Thread']),
    lines: [
      'Link two fighters so they share damage and healing.',
      'Shared healing is larger.',
      'The link also shares a small shield.',
      'Bound Mend heals every linked fighter.',
    ],
    whisper: 'In the Whisper slot, Link still shares healing with self.',
    fusionVariant: 1,
    tags: ['Thread'],
  }),
  rootmother: make({
    id: 'rootmother',
    name: 'Rootmother',
    archetype: 'wildkin',
    role: 'support',
    secondaryRole: null,
    fantasy: 'Grows healing groves. The longer a grove stands, the more it heals.',
    profile: { ...SUPPORT, heal: 18 },
    ...supportKit('rootmother', ['Grove', 'Tend', 'Deep Root'], ['Summon']),
    lines: [
      'A grove heals more the longer it stands.',
      'The grove grows faster.',
      'Tend also shields.',
      'Deep Root regrows a fallen grove once.',
    ],
    whisper: 'In the Whisper slot, Grove still heals a little.',
    fusionVariant: 0,
    tags: ['Summon'],
  }),
  packcaller: make({
    id: 'packcaller',
    name: 'Packcaller',
    archetype: 'wildkin',
    role: 'damage',
    secondaryRole: 'tank',
    fantasy: 'Commands a pack of 3 beasts that can be given orders independently.',
    profile: { ...DAMAGE, hp: 48, attack: 28 },
    ...damageKit('packcaller', ['Bite', 'Pack', 'Alpha'], ['Beast', 'Summon']),
    lines: [
      'Three beasts take orders and bite.',
      'Beasts live longer.',
      'One beast can guard.',
      'Alpha makes the pack focus one target.',
    ],
    whisper: 'In the Whisper slot, one beast still bites.',
    fusionVariant: 1,
    tags: ['Beast', 'Summon'],
  }),
  barkhide: make({
    id: 'barkhide',
    name: 'Barkhide',
    archetype: 'wildkin',
    role: 'tank',
    secondaryRole: 'control',
    fantasy: 'Gains layered bark armor that peels off and leaves thorny ground.',
    profile: TANK,
    ...tankKit('barkhide', ['Bark', 'Peel', 'Thorn'], ['Shield']),
    lines: [
      'Bark layers absorb hits, then peel off as thorns.',
      'One more bark layer.',
      'Thorns slow foes that walk them.',
      'Peel drops every layer at once.',
    ],
    whisper: 'In the Whisper slot, Bark still absorbs one hit.',
    fusionVariant: 2,
    tags: ['Shield'],
  }),
  sporelord: make({
    id: 'sporelord',
    name: 'Sporelord',
    archetype: 'wildkin',
    role: 'control',
    secondaryRole: 'damage',
    fantasy: 'Spreads spore clouds that stack poison and confusion.',
    profile: CONTROL,
    ...controlKit('sporelord', ['Spore', 'Cloud', 'Bloom'], ['Poison']),
    lines: [
      'Spore clouds stack poison.',
      'Poison stacks higher.',
      'Cloud also confuses, so foes miss more.',
      'Bloom pops every poison stack.',
    ],
    whisper: 'In the Whisper slot, Spore still applies one poison stack.',
    fusionVariant: 0,
    tags: ['Poison'],
  }),
  seasonturner: make({
    id: 'seasonturner',
    name: 'Seasonturner',
    archetype: 'wildkin',
    role: 'support',
    secondaryRole: 'damage',
    fantasy: 'Cycles Spring, Summer, Autumn, and Winter stances, each with a different kit.',
    profile: SUPPORT,
    ...supportKit('seasonturner', ['Turn', 'Stance', 'Solstice'], ['Beast']),
    lines: [
      'Cycle Spring, Summer, Autumn, and Winter. Each stance changes the kit.',
      'Stances last longer.',
      'Summer hits harder. Winter shields.',
      'Solstice lets you hold one stance.',
    ],
    whisper: 'In the Whisper slot, Turn still grants the current stance once.',
    fusionVariant: 1,
    tags: ['Beast'],
  }),
  stormhorn: make({
    id: 'stormhorn',
    name: 'Stormhorn',
    archetype: 'wildkin',
    role: 'damage',
    secondaryRole: null,
    fantasy: 'Charges up storms by moving, then calls lightning down.',
    profile: DAMAGE,
    ...damageKit('stormhorn', ['Charge', 'Bolt', 'Downburst'], ['Beast']),
    // Lightning ignores armor, and the hits sit above the shared damage band.
    // That is the Wildkin true-damage answer to Ironhide: a level-12 storm clears it, a level-6 hybrid does not.
    abilities: [
      ability('stormhorn_a', 'Charge', 'damage', 1.8, 6, ['Beast'], 1),
      ability('stormhorn_b', 'Bolt', 'damage', 1.5, 8, ['Beast'], 1),
    ],
    ultimate: ability('stormhorn_ult', 'Downburst', 'damage', 3.36, 20, ['Beast'], 1),
    lines: [
      'Moving charges a storm. Lightning spends it.',
      'The storm charges faster.',
      'Bolt hits an extra foe.',
      'Downburst spends the whole storm.',
    ],
    whisper: 'In the Whisper slot, Bolt still strikes once.',
    fusionVariant: 2,
    tags: ['Beast'],
  }),
  mosswarden: make({
    id: 'mosswarden',
    name: 'Mosswarden',
    archetype: 'wildkin',
    role: 'tank',
    secondaryRole: 'support',
    fantasy: 'Turns into a moss golem. Slow, but heals itself from terrain.',
    profile: { ...TANK, heal: 10, attack: 7 },
    ...tankKit('mosswarden', ['Golem', 'Mend', 'Moss'], ['Summon', 'Shield']),
    lines: [
      'Become a moss golem. Terrain heals you.',
      'Terrain heals more.',
      'Mend also shields.',
      'Moss regrows bark after a heavy hit.',
    ],
    whisper: 'In the Whisper slot, Golem still heals a little from the ground.',
    fusionVariant: 0,
    tags: ['Summon', 'Shield'],
  }),
  hivekeeper: make({
    id: 'hivekeeper',
    name: 'Hivekeeper',
    archetype: 'wildkin',
    role: 'support',
    secondaryRole: 'control',
    fantasy: 'Sends bee swarms that carry buffs to allies or stings to enemies.',
    profile: SUPPORT,
    ...supportKit('hivekeeper', ['Swarm', 'Sting', 'Honey'], ['Beast', 'Summon']),
    lines: [
      'Bees carry a buff or a sting.',
      'The swarm is larger.',
      'Sting also slows.',
      'Honey heals and stings in one pass.',
    ],
    whisper: 'In the Whisper slot, Swarm still delivers one sting or one buff.',
    fusionVariant: 1,
    tags: ['Beast', 'Summon'],
  }),
  debtcollector: make({
    id: 'debtcollector',
    name: 'Debtcollector',
    archetype: 'shadecaller',
    role: 'damage',
    secondaryRole: 'tank',
    fantasy: 'Damage taken becomes Debt it can call in for burst damage.',
    profile: { hp: 48, attack: 26, armor: 6, armorBreak: 28, heal: 0 },
    ...breakKit('debtcollector', ['Ledger', 'Call', 'Collection'], ['Shadow']),
    lines: [
      'Damage taken becomes Debt. Call it in as burst.',
      'More of the hit is stored.',
      'Call also breaks armor.',
      'Collection spends the whole ledger.',
    ],
    whisper: 'In the Whisper slot, Ledger still stores a little Debt.',
    fusionVariant: 0,
    tags: ['Shadow', 'Armor-break'],
  }),
  echo: make({
    id: 'echo',
    name: 'Echo Dancer',
    archetype: 'shadecaller',
    role: 'damage',
    secondaryRole: null,
    fantasy: 'After-images repeat the last strike.',
    profile: { hp: 36, attack: 32, armor: 4, armorBreak: 0, heal: 0 },
    abilities: [
      ability('echo_flurry', 'Flurry', 'damage', 1.5, 6, ['Shadow']),
      ability('echo_afterimage', 'Afterimage', 'damage', 1.25, 8, ['Shadow']),
    ],
    ultimate: ability('echo_final', 'Final Echo', 'damage', 2.8, 20, ['Shadow'], 0.25),
    lines: [
      'After-images repeat the last strike.',
      'One more image.',
      'Images hit the same target.',
      'Final Echo repeats the last three strikes.',
    ],
    whisper: 'In the Whisper slot, one after-image still repeats the last hit.',
    fusionVariant: 1,
    tags: ['Shadow'],
    preset: { abilityId: 'echo_flurry', condition: { kind: 'always' }, target: 'lowestHp', priority: 40 },
  }),
  lanternless: make({
    id: 'lanternless',
    name: 'Lanternless',
    archetype: 'shadecaller',
    role: 'damage',
    secondaryRole: 'control',
    fantasy: 'Turns invisible in darkness and puts out light sources.',
    profile: DAMAGE,
    ...damageKit('lanternless', ['Snuff', 'Dark', 'Blind'], ['Shadow']),
    lines: [
      'Put out lights and strike from the dark.',
      'The dark lasts longer.',
      'Snuff also slows.',
      'Blind is a heavy hit from stealth.',
    ],
    whisper: 'In the Whisper slot, Snuff still puts out one light.',
    fusionVariant: 2,
    tags: ['Shadow'],
  }),
  hollowshepherd: make({
    id: 'hollowshepherd',
    name: 'Hollow Shepherd',
    archetype: 'shadecaller',
    role: 'control',
    secondaryRole: 'damage',
    fantasy: 'Raises the echoes of fallen enemies as short-lived thralls.',
    profile: CONTROL,
    ...controlKit('hollowshepherd', ['Raise', 'Thrall', 'Dirge'], ['Shadow', 'Summon']),
    lines: [
      'Fallen foes rise as short-lived thralls.',
      'Thralls last longer.',
      'Thralls focus the healer type.',
      'Dirge raises every recent fall.',
    ],
    whisper: 'In the Whisper slot, Raise still lifts one echo.',
    fusionVariant: 0,
    tags: ['Shadow', 'Summon'],
  }),
  grave: make({
    id: 'grave',
    name: 'Grave Tender',
    archetype: 'shadecaller',
    role: 'support',
    secondaryRole: null,
    fantasy: 'Mends with shadow when the braid frays.',
    profile: { hp: 60, attack: 8, armor: 6, armorBreak: 0, heal: 22 },
    abilities: [
      ability('grave_mend', 'Mend', 'heal', 5.5, 10, ['Shadow']),
      ability('grave_shroud', 'Shroud', 'shield', 0.14, 14, ['Shadow', 'Shield']),
    ],
    ultimate: ability('grave_lastrite', 'Last Rite', 'heal', 9, 24, ['Shadow']),
    lines: [
      'Mend with shadow.',
      'Mend restores more.',
      'Shroud is thicker.',
      'Last Rite can bring you back once, at a cost to max HP.',
    ],
    whisper: 'In the Whisper slot, Mend still fires when HP is low.',
    fusionVariant: 1,
    tags: ['Shadow', 'Shield'],
    preset: { abilityId: 'grave_mend', condition: { kind: 'selfHpBelow', pct: 55 }, target: 'self', priority: 11 },
  }),
  umbralanchor: make({
    id: 'umbralanchor',
    name: 'Umbral Anchor',
    archetype: 'shadecaller',
    role: 'tank',
    secondaryRole: 'control',
    fantasy: 'Becomes a shadow pillar that absorbs damage and roots attackers.',
    profile: TANK,
    ...tankKit('umbralanchor', ['Pillar', 'Root', 'Still Shadow'], ['Shadow', 'Shield']),
    lines: [
      'Become a shadow pillar. It absorbs damage and roots attackers.',
      'The pillar absorbs more.',
      'Root lasts longer.',
      'Still Shadow holds a boss cast.',
    ],
    whisper: 'In the Whisper slot, Pillar still absorbs one hit.',
    fusionVariant: 2,
    tags: ['Shadow', 'Shield'],
  }),
  whisperthief: make({
    id: 'whisperthief',
    name: 'Whisperthief',
    archetype: 'shadecaller',
    role: 'control',
    secondaryRole: 'damage',
    fantasy: 'Steals an enemy skill and uses it once.',
    profile: CONTROL,
    ...controlKit('whisperthief', ['Steal', 'Once', 'Echoed Skill'], ['Shadow', 'Curse']),
    lines: [
      'Steal an enemy skill and use it once.',
      'The stolen skill hits harder.',
      'Steal prefers a healer type.',
      'Echoed Skill can be used twice.',
    ],
    whisper: 'In the Whisper slot, Steal still copies one skill.',
    fusionVariant: 0,
    tags: ['Shadow', 'Curse'],
  }),
  dusk: make({
    id: 'dusk',
    name: 'Duskbinder',
    archetype: 'shadecaller',
    role: 'control',
    secondaryRole: 'damage',
    fantasy: 'Curses that bite every nearby foe.',
    profile: { hp: 40, attack: 22, armor: 4, armorBreak: 0, heal: 0 },
    abilities: [
      ability('dusk_curse', 'Curse', 'aoe', 0.85, 8, ['Curse', 'Shadow']),
      ability('dusk_hex', 'Hex', 'damage', 1.3, 8, ['Curse']),
    ],
    ultimate: ability('dusk_spread', 'Spreading Dusk', 'aoe', 1.5, 20, ['Curse', 'Shadow']),
    lines: [
      'Curses bite nearby foes.',
      'Curses spread to one more target.',
      'A curse spreads farther when the host dies.',
      'Each Thread on the host adds another spread.',
    ],
    whisper: 'In the Whisper slot, Curse still hits two foes.',
    fusionVariant: 1,
    tags: ['Curse', 'Shadow'],
    preset: { abilityId: 'dusk_curse', condition: { kind: 'enemyCountAtLeast', count: 2 }, target: 'highestHp', priority: 30 },
  }),
};

export const SUBSPEC_LIST: SubSpecDef[] = Object.values(SUBSPECS);

/** The only sub-spec allow-list. The picker, the server, and the SQL check all use this. */
export const SUBSPEC_IDS: SubSpecId[] = SUBSPEC_LIST.map((spec) => spec.id);

export const ARCHETYPE_LABEL: Record<ArchetypeId, string> = {
  ironbound: 'Ironbound',
  veilweaver: 'Veilweaver',
  wildkin: 'Wildkin',
  shadecaller: 'Shadecaller',
};

export const SLOT_LABEL: Record<SlotId, { mark: string; weight: string }> = {
  primary: { mark: 'Heart Mark', weight: '50%' },
  secondary: { mark: 'Hand Mark', weight: '30%' },
  tertiary: { mark: 'Whisper Mark', weight: '20%' },
};

/** How many armor stacks a Sunder-type ability removes, before fusion. */
export const SUNDER_STRIP: Record<SlotId, number> = {
  primary: 4,
  secondary: 2,
  tertiary: 1,
};
